import forEach from "lodash/forEach";
import mapValues from "lodash/mapValues";
import moment from "moment";
import { hoursToMinutes, minutesToHours } from "PFCore/helpers/date";
import { StandardRangeV2 } from "PFTypes/availability";
import { DurationMode } from "PFTypes/booking";

import { AvailabilityTableData, AvailabilityTableRow, Week } from "./types";

type AvailabilityRange = {
  start: number;
  end: number;
  value: number;
};

const createRule = (range: AvailabilityRange, weeks: Week[]): StandardRangeV2 => ({
  start: weeks[range.start].startDate,
  end: weeks[range.end].endDate,
  duration: {
    mode: DurationMode.Hours,
    value: hoursToMinutes(range.value)
  }
});

export const transformToAvailabilityRules = (
  availabilityTableData: AvailabilityTableData,
  weeks: Week[]
): Record<string, StandardRangeV2[]> =>
  mapValues(availabilityTableData, (weekData) => {
    const rules: StandardRangeV2[] = [];
    let currentRange: AvailabilityRange | null = null;

    forEach(weekData, (value, weekIndexStr) => {
      const weekIndex = Number(weekIndexStr);
      const canExtendRange = value && currentRange?.value === value && weekIndex === currentRange.end + 1;

      if (!canExtendRange && currentRange) {
        rules.push(createRule(currentRange, weeks));
        currentRange = null;
      }

      if (!value) {
        return;
      }

      if (canExtendRange) {
        currentRange!.end = weekIndex;
      } else {
        currentRange = { start: weekIndex, end: weekIndex, value };
      }
    });

    if (currentRange) {
      rules.push(createRule(currentRange, weeks));
    }

    return rules;
  });

export const transformFromAvailabilityRules = (
  rule: StandardRangeV2,
  weeks: Week[]
): AvailabilityTableRow => {
  const weekData: AvailabilityTableRow = {};

  if (!rule.start || !rule.end || !rule.duration) {
    return weekData;
  }

  const ruleStart = moment.utc(rule.start);
  const ruleEnd = moment.utc(rule.end);
  const hours = minutesToHours(rule.duration.value);

  weeks.forEach(({ startDate, endDate }, weekIndex) => {
    if (ruleStart.isSameOrBefore(moment.utc(endDate)) && ruleEnd.isSameOrAfter(moment.utc(startDate))) {
      weekData[weekIndex] = hours;
    }
  });

  return weekData;
};
